import { useAuthActions } from '@convex-dev/auth/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { api } from '@repo/backend';
import { useQuery } from 'convex/react';
import { useEffect, useState } from 'react';
import { normalizeRedirectTarget } from '@/lib/auth';
import { toUserFriendlyErrorMessage } from '@/lib/errors';

export const Route = createFileRoute('/sign-in')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = normalizeRedirectTarget(search.redirect);
    return redirect ? { redirect } : {};
  },
  component: SignInPage,
});

function SignInPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const { signIn } = useAuthActions();
  const currentUser = useQuery(api.users.queries.me);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const redirectTarget = redirect ?? '/';

  useEffect(() => {
    if (currentUser) {
      void navigate({ to: redirectTarget });
    }
  }, [currentUser, navigate, redirectTarget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await signIn('password', {
        flow: 'signIn',
        email: email.trim().toLowerCase(),
        password,
        redirectTo: redirectTarget,
      });

      if (result.redirect) {
        window.location.assign(result.redirect.toString());
        return;
      }

      await navigate({ to: redirectTarget });
    } catch (error) {
      setErrorMessage(
        toUserFriendlyErrorMessage(error, 'Unable to sign in right now. Please try again.'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-8 bg-background relative overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="border border-iron-800 bg-iron-900/80 backdrop-blur-sm p-8 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-iron-800 group-hover:bg-secondary/50 transition-colors" />
          <div className="absolute top-0 left-0 w-[2px] h-full bg-iron-800 group-hover:bg-gradient-to-b group-hover:from-secondary/50 group-hover:to-transparent transition-all" />

          <div className="text-center mb-8">
            <h1 className="font-epic text-3xl tracking-[0.1em] text-white uppercase mb-2">
              Sign In
            </h1>
            <p className="text-iron-400 text-xs tracking-widest uppercase mb-4">
              Enter the Grand Hall
            </p>
            <div className="w-12 h-1 bg-secondary mx-auto shadow-[0_0_8px_rgba(241,198,41,0.6)]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-[10px] tracking-[0.2em] uppercase font-bold text-iron-300"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-12 bg-iron-950 border border-iron-800 px-4 text-iron-100 placeholder:text-iron-600 focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary/50 transition-all font-sans"
                placeholder="knight@westeros.com"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block text-[10px] tracking-[0.2em] uppercase font-bold text-iron-300"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-12 bg-iron-950 border border-iron-800 px-4 text-iron-100 placeholder:text-iron-600 focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary/50 transition-all font-sans"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-secondary text-secondary-foreground font-bold tracking-[0.2em] uppercase text-xs hover:bg-secondary/90 hover:shadow-[0_0_15px_rgba(241,198,41,0.4)] disabled:cursor-not-allowed disabled:opacity-70 transition-all cursor-pointer relative overflow-hidden group"
            >
              <span className="relative z-10">
                {isSubmitting ? 'Opening Gate' : 'Enter Realm'}
              </span>
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            </button>

            {errorMessage ? (
              <div className="border border-primary/40 bg-primary/10 px-4 py-3 text-xs text-primary">
                {errorMessage}
              </div>
            ) : null}
          </form>

          <div className="mt-8 text-center border-t border-iron-800 pt-6">
            <p className="text-xs text-iron-400">
              New to the auction?{' '}
              <Link
                to="/register"
                search={redirect ? { redirect } : {}}
                className="text-secondary hover:text-white transition-colors uppercase tracking-wider font-bold ml-1"
              >
                Pledge Fealty
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
